/**
 * Centralized API Service with Progressive Backoff
 * Provides unified error handling, retry logic, and circuit breaker pattern
 */

import logger from './logger';

class ApiService {
  constructor() {
    // Circuit breaker state per endpoint
    this.circuitBreakers = new Map();
    
    // Configuration
    this.config = {
      maxRetries: 3,
      baseDelay: 1000, // 1 second
      maxDelay: 30000, // 30 seconds
      backoffMultiplier: 2,
      circuitBreakerThreshold: 3, // Open circuit after 3 failures (faster for connection refused)
      circuitBreakerTimeout: 60000 // 1 minute before attempting again
    };
  }

  /**
   * Calculate exponential backoff delay
   * @param {number} attempt - Current attempt number (0-indexed)
   * @returns {number} Delay in milliseconds
   */
  calculateBackoff(attempt) {
    const delay = Math.min(
      this.config.baseDelay * Math.pow(this.config.backoffMultiplier, attempt),
      this.config.maxDelay
    );
    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 1000;
    return delay + jitter;
  }

  /**
   * Check if circuit breaker is open for an endpoint
   * @param {string} endpoint - API endpoint
   * @returns {boolean} True if circuit is open
   */
  isCircuitOpen(endpoint) {
    const breaker = this.circuitBreakers.get(endpoint);
    if (!breaker) return false;
    
    if (breaker.state === 'open') {
      const now = Date.now();
      if (now - breaker.lastFailure < this.config.circuitBreakerTimeout) {
        return true; // Still open
      }
      // Timeout expired, try half-open
      breaker.state = 'half-open';
      breaker.failureCount = 0;
    }
    
    return false;
  }

  /**
   * Record success in circuit breaker
   * @param {string} endpoint - API endpoint
   */
  recordSuccess(endpoint) {
    const breaker = this.circuitBreakers.get(endpoint);
    if (breaker) {
      if (breaker.state === 'half-open') {
        breaker.state = 'closed';
      }
      breaker.failureCount = 0;
      breaker.lastSuccess = Date.now();
    }
  }

  /**
   * Record failure in circuit breaker
   * @param {string} endpoint - API endpoint
   * @param {Error} error - Optional error object to check if it should be excluded
   */
  recordFailure(endpoint, error = null) {
    // Don't record client/config errors as circuit breaker failures
    if (error) {
      const status = error.status || error.response?.status;
      if (status === 403) {
        return;
      }
      if (status === 404) {
        return;
      }
      if (status === 503) {
        return;
      }
    }
    
    let breaker = this.circuitBreakers.get(endpoint);
    if (!breaker) {
      breaker = {
        state: 'closed',
        failureCount: 0,
        lastFailure: 0,
        lastSuccess: 0
      };
      this.circuitBreakers.set(endpoint, breaker);
    }
    
    breaker.failureCount++;
    breaker.lastFailure = Date.now();
    
    if (breaker.failureCount >= this.config.circuitBreakerThreshold) {
      breaker.state = 'open';
      logger.warn('API-SERVICE', 'Circuit breaker opened', { 
        endpoint: endpoint.substring(0, 50), 
        failures: breaker.failureCount 
      });
    }
  }

  /**
   * Reset circuit breaker for a specific endpoint or all endpoints
   * @param {string|null} endpoint - API endpoint to reset, or null to reset all
   */
  resetCircuitBreaker(endpoint = null) {
    if (endpoint) {
      const breaker = this.circuitBreakers.get(endpoint);
      if (breaker) {
        breaker.state = 'closed';
        breaker.failureCount = 0;
        breaker.lastFailure = 0;
        logger.info('API-SERVICE', 'Circuit breaker reset', { endpoint: endpoint.substring(0, 50) });
      }
    } else {
      // Reset all circuit breakers
      this.circuitBreakers.clear();
      logger.info('API-SERVICE', 'All circuit breakers reset');
    }
  }

  /**
   * Check if error is a "container missing" error that should be handled gracefully
   * @param {Error} error - Error object
   * @returns {boolean} True if it's a container missing error
   */
  isContainerMissingError(error) {
    if (!error) return false;
    
    const errorStr = String(error.message || error.error || '').toLowerCase();
    const status = error.status || error.response?.status;
    
    // Check for container missing indicators
    const containerMissingIndicators = [
      'owner resource does not exist',
      'not found',
      'does not exist',
      'main tenant not set',
      'container_missing',
      'no accessible tenants'
    ];
    
    // 400 errors with container missing messages should be handled gracefully
    if (status === 400 && containerMissingIndicators.some(indicator => errorStr.includes(indicator))) {
      return true;
    }
    
    // Check response data for container_missing flag
    if (error.data?.container_missing === true || error.data?.code === 'MAIN_TENANT_NOT_SET') {
      return true;
    }
    
    return false;
  }

  /**
   * True when the request never reached the server (no HTTP response).
   * For these, retrying POST /knowledge is safe (no duplicate could have been created).
   */
  isNetworkLevelFailure(error) {
    if (!error) return false;
    const status = error.status ?? error.response?.status;
    if (status != null && status !== undefined) return false;
    const msg = String(error.message || error.error || '').toLowerCase();
    const name = String(error.name || '').toLowerCase();
    return msg.includes('failed to fetch') ||
      msg.includes('networkerror') ||
      msg.includes('request timeout') ||
      msg.includes('aborted') ||
      msg.includes('err_network_changed') ||
      name === 'typeerror' ||
      name === 'aborterror';
  }

  /**
   * Check if error is retryable
   * @param {Error} error - Error object
   * @returns {boolean} True if error should be retried
   */
  isRetryableError(error, endpoint = '', method = '') {
    if (!error) return false;
    
    // Container missing errors should NOT be retried (handled gracefully)
    if (this.isContainerMissingError(error)) {
      return false;
    }
    
    const errorMessage = String(error.message || error.error || '').toLowerCase();
    const errorName = String(error.name || '').toLowerCase();
    const status = error.status || error.response?.status;

    if (status === 404) {
      return false;
    }

    // Profile activity: ClickHouse/query errors return 500; retrying only delays the UI (multi-second backoff).
    const endpointStrEarly = typeof endpoint === 'string' ? endpoint : '';
    if (
      status === 500 &&
      (endpointStrEarly.includes('/me/activity') ||
        endpointStrEarly.includes('/logs/system/me/events'))
    ) {
      return false;
    }

    // POST /knowledge: only allow retry when request never reached the server (network/timeout).
    // If we got any HTTP response, do not retry to avoid duplicate records.
    if (endpoint && endpoint.includes('/knowledge') && method === 'POST') {
      if (this.isNetworkLevelFailure(error)) return true;
      return false;
    }
    
    // Connection refused errors should NOT be retried - fail fast
    if (errorMessage.includes('connection refused') || 
        errorMessage.includes('err_connection_refused') ||
        errorName.includes('connectionrefused')) {
      return false;
    }
    
    // Other network/timeout errors (no status): retry
    if (errorMessage.includes('failed to fetch') ||
        errorMessage.includes('networkerror') ||
        errorMessage.includes('request timeout') ||
        errorMessage.includes('err_network_changed')) {
      return true;
    }
    
    if (!status) {
      return !errorMessage.includes('refused');
    }

    if (status === 503) {
      return false;
    }

    if (status >= 500) return true;
    if (status === 408 || status === 429) return true;
    return false;
  }

  /**
   * Execute API request with progressive backoff and circuit breaker
   * @param {Function} apiClient - API client instance
   * @param {string} endpointOrMethod - API endpoint path or method name (e.g., 'listWorkspaces')
   * @param {object} options - Request options
   * @returns {Promise<any>} API response
   */
  async executeWithBackoff(apiClient, endpointOrMethod, options = {}) {
    const endpoint = endpointOrMethod;
    
    // Check circuit breaker
    if (this.isCircuitOpen(endpoint)) {
      logger.warn('API-SERVICE', 'Circuit breaker open, returning fallback', { 
        endpoint: endpoint.substring(0, 50) 
      });
      
      // Return graceful fallback for container missing errors
      // Handle all /v1/ endpoints, /tenants endpoints, and workspace listing
      if (endpoint.includes('/v1/') || 
          endpoint.includes('/tenants/main/details') || 
          endpoint.includes('/tenants') || 
          endpoint === 'listWorkspaces' || 
          endpoint.includes('/api/customer_portal')) {
        return {
          success: true,
          data: [],
          container_missing: true,
          error: 'Service temporarily unavailable'
        };
      }
      
      throw new Error(`Circuit breaker open for ${endpoint}`);
    }
    
    // Execute request directly with retry logic (no queue)
    return this.executeRequestInternal(apiClient, endpointOrMethod, options);
  }

  /**
   * Internal method to execute request with retry logic
   * @param {Function} apiClient - API client instance
   * @param {string} endpointOrMethod - API endpoint path or method name
   * @param {object} options - Request options
   * @returns {Promise<any>} API response
   */
  async executeRequestInternal(apiClient, endpointOrMethod, options = {}) {
    const endpoint = endpointOrMethod;
    
    // Execute with retry logic
    const executeRequest = async (attempt = 0) => {
      try {
        let promise;
        
        // Support custom fetch function (from apiClient.apiRequest)
        if (options._executeFetch && typeof options._executeFetch === 'function') {
          promise = options._executeFetch();
        }
        // Support direct method calls
        else if (options.method === 'CALL' && typeof apiClient[endpointOrMethod] === 'function') {
          // Direct method call (e.g., apiClient.listWorkspaces(params))
          // Note: This will still go through apiRequest which uses centralized service
          promise = apiClient[endpointOrMethod](options.params || {});
        } 
        // Default: apiRequest call (will use centralized service)
        else {
          promise = apiClient.apiRequest(endpointOrMethod, options);
        }
        
        const result = await promise;
        
        // Record success
        this.recordSuccess(endpoint);
        
        return result;
        
      } catch (error) {
        // Handle container missing errors gracefully
        if (this.isContainerMissingError(error)) {
          logger.debug('API-SERVICE', 'Container missing, returning fallback', { 
            endpoint: endpoint.substring(0, 50) 
          });
          this.recordSuccess(endpoint); // Don't count as failure
          
          if (endpoint.includes('/tenants/main/details') || endpoint.includes('/tenants') || 
              endpoint === 'listWorkspaces' || endpoint.includes('/api/customer_portal')) {
            return {
              success: true,
              data: [],
              container_missing: true,
              error: error.message || 'Container not yet initialized'
            };
          }
          
          // Generic graceful fallback
          return {
            success: true,
            data: [],
            container_missing: true,
            error: error.message || 'Resource not yet initialized'
          };
        }
        
        // POST /knowledge: prevent retry only when the request reached the server (we got a response).
        // On network-level failures (Failed to fetch, ERR_NETWORK_CHANGED, timeout), no response was
        // received so no duplicate could have been created — allow retry.
        const method = options.method || 'GET';
        const isKnowledgePost = endpoint.includes('/knowledge') && method === 'POST';
        const networkFailure = this.isNetworkLevelFailure(error);

        if (isKnowledgePost && !networkFailure) {
          const errorData = error.data || {};
          const knowledgeId = errorData.knowledge_id || errorData.id || errorData.knowledge_item?.id;
          const errorMessage = String(error.message || errorData.error || errorData.message || '').toLowerCase();
          const recordCreated = knowledgeId ||
                               errorMessage.includes('failed to store content') ||
                               errorMessage.includes('failed to ensure knowledge-content') ||
                               errorMessage.includes('knowledge-content container');

          logger.warn('API-SERVICE', 'POST /knowledge request failed - preventing retry to avoid duplicate records', {
            endpoint: endpoint.substring(0, 50),
            knowledgeId: knowledgeId || 'none',
            recordCreated: recordCreated,
            error: errorMessage.substring(0, 150),
            method,
            status: error.status,
            attempt: attempt + 1
          });
          this.recordFailure(endpoint, error);
          throw error;
        }

        if (isKnowledgePost && networkFailure) {
          logger.info('API-SERVICE', 'POST /knowledge network/timeout failure - allowing retry (no response received)', {
            endpoint: endpoint.substring(0, 50),
            attempt: attempt + 1,
            maxRetries: this.config.maxRetries + 1
          });
        }

        // Check if retryable (pass endpoint and method for knowledge upload check)
        const isRetryable = this.isRetryableError(error, endpoint, method);
        const shouldRetry = attempt < this.config.maxRetries && isRetryable;
        
        if (!shouldRetry) {
          // Record failure (but exclude 403 errors from circuit breaker)
          this.recordFailure(endpoint, error);
          
          // Don't throw for 400 errors - return graceful response
          if (error.status >= 400 && error.status < 500 && !isRetryable) {
            const endpointStr = typeof endpoint === 'string' ? endpoint : '';
            const isExpectedNewUser = (error.status === 403 || error.status === 400) && (
              endpointStr.includes('/v1/tenants') ||
              endpointStr.includes('/v1/resources') ||
              endpointStr.includes('/v1/provision-featured-products') ||
              endpointStr.includes('/v1/users/get-or-create-by-email') ||
              endpointStr === 'listWorkspaces' ||
              endpointStr === 'listResources'
            );
            if (isExpectedNewUser) {
              logger.info('API-SERVICE', 'Expected for new user (no tenant/user doc yet) - guiding to onboarding', {
                endpoint: endpointStr.substring(0, 50),
                status: error.status
              });
            } else {
              logger.warn('API-SERVICE', 'Client error', { 
                endpoint: endpointStr.substring(0, 50),
                status: error.status,
                message: error.message
              });
            }
            return {
              success: false,
              error: error.message || 'Request failed',
              data: []
            };
          }
          
          throw error;
        }
        
        // Calculate backoff delay; for network failures on first retry use a minimum delay
        // so reloads (ERR_NETWORK_CHANGED / Failed to fetch) have time to stabilize
        let delay = this.calculateBackoff(attempt);
        if (this.isNetworkLevelFailure(error) && attempt === 0) {
          const minDelayOnReload = 600;
          delay = Math.max(delay, minDelayOnReload);
        }
        logger.info('API-SERVICE', 'Retrying request', {
          endpoint: endpoint.substring(0, 50),
          attempt: attempt + 1,
          maxRetries: this.config.maxRetries + 1,
          delay_ms: delay,
          ...(this.isNetworkLevelFailure(error) && {
            note: 'network failure (e.g. ERR_NETWORK_CHANGED) – retry is automatic and usually succeeds'
          })
        });

        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry
        return executeRequest(attempt + 1);
      }
    };
    
    return executeRequest();
  }
}

// Singleton instance
let apiServiceInstance = null;

/**
 * Get API service instance
 * @returns {ApiService} API service instance
 */
export function getApiService() {
  if (!apiServiceInstance) {
    apiServiceInstance = new ApiService();
    // Expose globally for debugging (only in development)
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      window.__resetCircuitBreakers = (endpoint = null) => {
        apiServiceInstance.resetCircuitBreaker(endpoint);
      };
      window.__getCircuitBreakerState = () => {
        const state = {};
        apiServiceInstance.circuitBreakers.forEach((breaker, endpoint) => {
          state[endpoint] = {
            state: breaker.state,
            failureCount: breaker.failureCount,
            lastFailure: breaker.lastFailure ? new Date(breaker.lastFailure).toISOString() : null,
            lastSuccess: breaker.lastSuccess ? new Date(breaker.lastSuccess).toISOString() : null
          };
        });
        return state;
      };
    }
  }
  return apiServiceInstance;
}

/**
 * Execute API request with progressive backoff
 * @param {Function} apiClient - API client instance
 * @param {string} endpoint - API endpoint
 * @param {object} options - Request options
 * @returns {Promise<any>} API response
 */
export async function apiRequestWithBackoff(apiClient, endpoint, options = {}) {
  const service = getApiService();
  return service.executeWithBackoff(apiClient, endpoint, options);
}

export default ApiService;
